import { createAction } from "redux-actions";
import get from "lodash.get";
import SockJS from "sockjs-client";
import Centrifuge from "centrifuge";
import * as types from "./types";
import CentrifugeService from "../services/CentrifugeService";
import {
  fetchLoadBids,
  fetchLoadById,
  fetchLoadNegotiations,
  newPendingUpdate,
} from "../screens/LoadsDetails/actions";
import {
  getAPIEndpoint,
  getCentrifugeAPIEndpoint,
} from "../utils/apiConstants";
import history from "../history";
import { statusLabelMap } from "../utils/constants";
import {
  requestAuctionTime,
  requestAuctionTimeCachedLoads,
  updateLoadListByStatus,
} from '../screens/Loads/actions'
import { incrementUpdates } from '../screens/Loads2/actions'
import { getDeliveryId } from '../utils/stringFormatter'
import { fetchIncidentById, fetchIncidentsByParams } from '../screens/Incidents/actions'
import { toFullCurrency } from '../utils/numberFormatters'
import { openNotification, setOffer, setTimer } from '../screens/LoadTable/actions'
import { incrementCountOfUpdates, setPendingModal } from '../screens/Pendings/actions'
import { handleReportGenerationMessage } from '../utils/application'
import { queryClient } from '../queryClient'
import { tenderingTransporterFeedbackMap } from '../utils/tendering'

const centrifugeOptions = {
  debug: true,
  url: getCentrifugeAPIEndpoint(),
  authEndpoint: `${getAPIEndpoint()}/centrifugo/auth`,
};

let centrifuge = {};

const connectCentrifuge = (onCentrifugeReady) => (dispatch) => {
  centrifuge.on("connect", (context) => {
    console.log("centrifuge connected!", context);
    setTimeout(() => {
      dispatch(requestAuctionTime());
    }, 2000);
    if (onCentrifugeReady) {
      onCentrifugeReady();
    }
  });
  centrifuge.on("error", (error) => {
    console.log(error);
  });
  centrifuge.connect();
};
const connectCentrifugeWithCachedLoads = (onCentrifugeReady) => (dispatch) => {
  centrifuge.on("connect", (context) => {
    console.log("centrifuge connected!", context);
    setTimeout(() => {
      dispatch(requestAuctionTimeCachedLoads());
    }, 2000);
    if (onCentrifugeReady) {
      onCentrifugeReady();
    }
  });
  centrifuge.on("error", (error) => {
    console.log(error);
  });
  centrifuge.connect();
};

export const centrifugeStatus = async () =>
  (await centrifuge._status) === undefined ? "DISCONNECT" : centrifuge._status;

const tickTimer = () => (dispatch) => {
  dispatch(createAction(types.TICK_AUCTION_TIMER)());
};

let timer = null;
export const startAuctionTimer = () => (dispatch) => {
  if (timer === null) {
    clearInterval(timer);
    timer = setInterval(() => {
      dispatch(tickTimer());
    }, 1000);
    dispatch(tickTimer());
  }
};

export const stopAuctionTimer = () => () => {
  clearInterval(timer);
  timer = null;
};

export const startCentrifuge =
  (onCentrifugeReady, isCachedLoads) => (dispatch, state) => {
    CentrifugeService.getToken()
      .then((data) => {
        centrifuge = new Centrifuge({
          ...centrifugeOptions,
          user: data.user,
          timestamp: data.timestamp,
          token: data.token,
          authHeaders: {
            Authorization: "bearer ".concat(state().user.access_token),
          },
          sockJS: SockJS,
        });
        if (isCachedLoads) {
          return dispatch(connectCentrifugeWithCachedLoads(onCentrifugeReady));
        }
        return dispatch(connectCentrifuge(onCentrifugeReady));
      })
      .catch((err) => {
        console.log(err);
      });
  };

const handleBidChannel = (obj, dispatch) => {
  const { bidConfigId, type } = obj;

  const path = get(history, "location.pathname", "");
  const isBidDetails = path.includes(`/bids/${bidConfigId}`);

  if (!isBidDetails) return;

  if (type === "bid_proposal") {
    dispatch(createAction("INCREMENT_PENDING_PROPOSAL_COUNT")());
  }
};

const onMessageReceived = (message, hashId) => (dispatch, state) => {
  if (message && message.data && message.data.input) {
    const obj = JSON.parse(message.data.input);
    const type = obj.type;

    if (message.channel.includes('channel_transporter_bid')) {
      handleBidChannel(obj, dispatch, state)
    }

    let unformattedHash =
      get(state(), `loadsDetails.mappedLoadsDetailsByHash.${hashId}.hashId`, null) ||
      get(state(), `loads.allMappedLoads.${hashId}.hashId`, null)

    const path = get(history, "location.pathname", "");

    if (message.channel.includes('channel_transporter_tendering')) {
      const { carrierId, type, shipmentOfferId } = obj
      const currentTransporterHash = get(state(), 'settings.transporterInfo.hashId', null)
      const path = get(history, 'location.pathname', '')

      const tenderingMessageTypes = [
        'tendering_refusal_offer',
        'tendering_expired_valid_offer',
        'tendering_expired_invalid_offer',
        'tendering_running_offer',
        'tendering_expired_offer',
      ]

      const typeConfig = tenderingTransporterFeedbackMap[type]

      const isTenderingMessage = tenderingMessageTypes.includes(type)
      const isCurrentOfferPage = path.includes(shipmentOfferId)
      const isLoadsPage = path === '/loads' || path === '/'
      const isCurrentTransporter = carrierId === currentTransporterHash

      if (isTenderingMessage && isCurrentOfferPage) {
        queryClient.invalidateQueries({ queryKey: ['offer', shipmentOfferId] })
      }

      if (isTenderingMessage && isLoadsPage) {
        const isRefusalOrAccept =
          type === 'tendering_refusal_offer' || type === 'tendering_accept_offer'

        if (isRefusalOrAccept && !isCurrentTransporter) {
          dispatch(incrementUpdates())
        }

        if (typeConfig && isCurrentTransporter) {
          dispatch(incrementUpdates())
          dispatch(
            createAction(types.SHOW_USER_FEEDBACK)({
              feedbackTitle: typeConfig.title,
              feedbackMessage: typeConfig.message(obj),
              feedbackType: 'NOTIFICATION',
              styleFeedbackType: typeConfig.styleFeedbackType,
              messageTimestamp: Date.now(),
              btnLabel: 'Ver carga',
              onClick: () => history.push(`/loads/${shipmentOfferId}/details`),
            })
          )
        }
      }
    }
    if (message.channel.includes('channel_transporter_shipping')) {
      handleReportGenerationMessage(obj, dispatch, createAction, types)
      unformattedHash = obj.shippingHash
      let path = get(history, 'location.pathname', '')
      // pendings
      if (path && path.endsWith("/")) {
        path = path.slice(0, -1);
      }
      unformattedHash = obj.shippingHash;
      if (
        type === "NEW_PENDENCIES" &&
        !path.includes(`/pendings`) &&
        !path.includes(`/details`)
      ) {
        dispatch(
          setPendingModal({
            type: "DETAIL",
            newUpdate: Date.now().toLocaleString(),
          })
        )
      }
      if (
        (type === "CLOSE_PENDENCIES" || type === "NEW_PENDENCIES") &&
        !path.includes(`/pendings`) &&
        !path.includes("/details")
      ) {
        dispatch(newPendingUpdate(type));
      }
      if (
        (type === "CLOSE_PENDENCIES" || type === "NEW_PENDENCIES") &&
        (path === `/loads/${obj?.shippingHash}/details` ||
          path === `/loads/${obj?.shippingHash}/details/delivery/${obj.shippingDeliveryId}` ||
          path === `/loads/${obj?.shippingHash}/details/grouped/${obj.shippingDeliveryId}`)
      ) {
        dispatch(newPendingUpdate(type));
      }
      if (
        (type === "CLOSE_PENDENCIES" || type === "NEW_PENDENCIES") &&
        path.includes(`/pendings`)
      ) {
        dispatch(incrementCountOfUpdates());
      }
    }

    const deliveryId = getDeliveryId(path);

    if (obj.type === "RESIDUALTIME") {
      const secs = obj.value;
      const days = secs / 3600 > 24 ? Math.floor(secs / 3600 / 24) : 0;
      let hours = Math.floor((secs / 3600) % 24);
      let minutes = Math.floor((secs / 60) % 60);
      let seconds = secs % 60;
      if (hours < 10) {
        hours = `0${hours}`;
      }
      if (minutes < 10) {
        minutes = `0${minutes}`;
      }
      if (seconds < 10) {
        seconds = `0${seconds}`;
      }
      dispatch(setTimer(hashId, obj.value));
      dispatch(
        createAction(types.CENTRIFUGE_UPDATE_AUCTION_TIME)({
          loadId: hashId,
          remainingTime: `${hours}:${minutes}:${seconds}`,
          daysLeft: `${days} dias`,
          rawValue: secs,
        })
      )
    } else if (obj.type === 'BID') {
      dispatch(setOffer(hashId, obj.value))
      if (path.includes(`${unformattedHash}/details`)) {
        dispatch(fetchLoadById(unformattedHash));
        dispatch(fetchLoadBids(unformattedHash));
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: "Novo lance no leilão.",
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
          })
        )
      } else if (path.includes('/old')) {
        dispatch(updateLoadListByStatus())
      } else if (path.includes('/') || path.includes('/loads')) {
        dispatch(incrementUpdates())
      }
      dispatch(
        createAction(types.POST_NEW_CENTRIFUGE_BID)({
          transporter: {
            id: obj.winner,
          },
          value: parseFloat(obj.value.replace(".", "").replace(",", ".")),
          date: obj.date,
          hashId,
        })
      )
    } else if (obj.type === 'UPDATE_SHIPPING_STATUS' || obj.type === 'UPDATE_DELIVERY_STATUS') {
      if (state().loadTable.viewTable) {
        dispatch(
          openNotification(
            `${obj.shippingExternalId} foi atualizada.`,
            'Caso deseje atualizar a tabela clique em atualizar.'
          )
        )
      }
      if (path.includes(`${unformattedHash}/details`)) {
        dispatch(createAction(types.IS_APPLICATION_LOADING)(true))
        setTimeout(() => dispatch(fetchLoadById(unformattedHash, deliveryId)), 2000)

        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: `Esta carga foi atualizada para o status ${
              statusLabelMap[obj.status]
            }.`,
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
          })
        )
      } else {
        if (path.includes('/old')) {
          dispatch(updateLoadListByStatus())
        } else if (path.includes('/') || path.includes('/loads')) {
          if (obj.status === 'WAIT_TRANSPORTER_ACCEPT' || obj.status === 'NEGOTIATION') {
            dispatch(incrementUpdates())
          } else if (obj.status === 'ASSIGNED_TO_TRANSPORTER') {
            dispatch(incrementUpdates())
          } else if (
            obj.status === "LINKED_TO_DRIVER_VEHICLE" ||
            obj.status === "IN_COLLECT" ||
            obj.status === "COLLECT_DONE"
          ) {
            dispatch(incrementUpdates());
          } else if (obj.status === "IN_TRANSIT") {
            dispatch(incrementUpdates());
          } else if (obj.status === "IN_DELIVERY") {
            dispatch(incrementUpdates());
          } else if (obj.status === "DELIVERED") {
            dispatch(incrementUpdates());
          } else if (obj.status === "FINISHED") {
            dispatch(incrementUpdates());
          } else if (obj.status === "CANCELED") {
            dispatch(incrementUpdates());
          }
        }
        let notificationPath = `/loads/${unformattedHash}/details/`;
        if (deliveryId) {
          notificationPath = `/loads/${unformattedHash}/details/delivery/${deliveryId}`;
        }
        if (obj.status !== "OFFER_COMPLETED") {
          dispatch(
            createAction(types.SHOW_USER_FEEDBACK)({
              feedbackMessage: `A carga ${obj.shippingExternalId} foi atualizada para o status ${
                statusLabelMap[obj.status]
              }.`,
              feedbackTitle: 'Informação',
              feedbackType: 'NOTIFICATION',
              styleFeedbackType: 'success',
              messageTimestamp: new Date().getTime(),
              btnLabel: "Ver carga",
              onClick: () => history.push(notificationPath),
            })
          )
        }
      }
    } else if (obj.type === "TYPE_MESSAGE_OCCURRENCE") {
      if (obj.agentType === "TRANSPORTER") {
        return null;
      }
      let infoMessage = "";
      if (obj.eventType === "OPENING") {
        infoMessage = `Uma nova ocorrência acaba de ser criada para a carga ${obj.shippingExternalId}`;
        if (path.includes("incidents")) {
          dispatch(fetchIncidentsByParams({}));
        }
      } else if (obj.eventType === "TREATMENT") {
        infoMessage = `Um novo comentário sobre a ocorrência ${obj.occurrenceId}
           da carga ${obj.shippingExternalId} acabou de ser feito.`;
      } else if (obj.eventType === "CLOSING") {
        infoMessage = `A ocorrência ${obj.occurrenceId} da carga ${obj.shippingExternalId} acabou de ser finalizada.`;
      }
      if (path.includes(`incident/${obj.occurrenceId}`)) {
        dispatch(fetchIncidentById(obj.occurrenceId));
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: infoMessage,
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
          })
        )
      } else {
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: infoMessage,
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
            btnLabel: "Ver ocorrência",
            onClick: () => history.push(`/incident/${obj.occurrenceId}`),
          })
        )
      }
    } else if (obj.type === "NEW_PROPOSAL") {
      if (path.includes(`${unformattedHash}/details`)) {
        dispatch(fetchLoadNegotiations(unformattedHash));
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: "Nova proposta recebida para este frete.",
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
          })
        )
      } else {
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: `A carga ${
              obj.shippingExternalId
            } recebeu uma nova proposta, no valor de ${toFullCurrency(
              obj.proposal
            )}. Clique aqui para visualizar.`,
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
            btnLabel: "Ver frete",
            onClick: () => history.push(`/loads/${unformattedHash}/details/`),
          })
        )
      }
    } else if (obj.type === 'ACCEPT_PROPOSAL') {
      if (path.includes(`${unformattedHash}/details`) && !path.includes('delivery')) {
        dispatch(fetchLoadById(unformattedHash))
        dispatch(
          createAction(types.SHOW_USER_FEEDBACK)({
            feedbackMessage: "Sua proposta foi aceita pelo embarcador.",
            feedbackTitle: "Informação",
            feedbackType: "NOTIFICATION",
            styleFeedbackType: "success",
            messageTimestamp: new Date().getTime(),
          })
        )
      }
    } else if (
      obj.type === "UPDATE_DELIVERY_RECEIVER_BY_TRANSPORTER" ||
      obj.type === "UPDATE_DELIVERY_RECEIVER_BY_SHIPPER"
    ) {
      if (
        (path.includes('/') || path.includes('/loads') || path.includes('/loads2')) &&
        !path.includes(`${unformattedHash}/details`)
      ) {
        dispatch(incrementUpdates());
        // dispatch(updateLoadListByStatus())
      }
    }
  }
  return null;
};

const formatChannelHash = (channel) => channel.replace(/\W/g, "");

export const subscribeChannels =
  (channels, cachedLoads) => async (dispatch, state) => {
    if (!channels?.length || typeof centrifuge.startAuthBatching !== "function") {
      return;
    }

    centrifuge.startAuthBatching();
    const unsubscribedChannels = [];

      channels.forEach((channel) => {
        const hash = formatChannelHash(channel);
        const channelId = get(state(), `centrifuge[${hash}]`, false);

        if (!channelId) {
          unsubscribedChannels.push({
            channel,
            onMessageReceived: (message) =>
              dispatch(onMessageReceived(message, hash)),
          });
        }
      });

      unsubscribedChannels.forEach((item) => {
        centrifuge.subscribe(item.channel, (message) =>
          dispatch(onMessageReceived(message, formatChannelHash(item.channel))),
        );
      });

      centrifuge.stopAuthBatching();
      dispatch(
        createAction(types.CENTRIFUGE_MULTIPLE_CHANNEL_SUBSCRIBED)(
          Object.assign(
            {},
            ...unsubscribedChannels.map((item) => ({
              [formatChannelHash(item.channel)]: true,
            })),
          ),
        ),
      );
      if (cachedLoads) {
        return dispatch(requestAuctionTimeCachedLoads());
      }
      dispatch(requestAuctionTime());
  };

export const subscribeOnChannel = (hashId) => (dispatch, state) => {
  const hash = formatChannelHash(hashId);
  const channel = "$".concat(hash);
  const channelId = get(state(), `centrifuge[${hash}]`, false);
  if (!channelId) {
    centrifuge.subscribe(channel, (message) =>
      dispatch(onMessageReceived(message, formatChannelHash(hash)))
    )
    dispatch(createAction(types.CENTRIFUGE_CHANNEL_SUBSCRIBED)(hash))
  }
  // dispatch(startCentrifuge(() => {
  //   if (hashId) {
  //     const channel = '$'.concat(hash)
  //     const channelId = get(state(), `centrifuge[${hash}]`, false)
  //     if (!channelId) {
  //       dispatch(createAction(types.CENTRIFUGE_CHANNEL_SUBSCRIBED)(hash))
  //       centrifuge.subscribe(channel,
  //         message => dispatch(onMessageReceived(message, hash)))
  //     }
  //   }
  // }))
}
export const getChannels =
  (isCachedLoads = false) =>
  (dispatch) => {
    CentrifugeService.getChannels()
      .then((channels) => {
        dispatch(subscribeChannels(channels, isCachedLoads))
      })
      .catch((err) => {
        console.log(err)
      })
  }
